import { useTranslations } from "@jugl-web/utils";
import React, { useEffect, useMemo, useState } from "react";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { ENABLED_ENTITY_MODULES_KEY } from "@jugl-web/utils/storage";
import { useWorkspaceFeatureCardData } from "@web-src/modules/workspace/hooks/useWorkspaceFeatureCardData/useWorkspaceFeatureCardData";
import isEqual from "lodash/isEqual";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { UserRole } from "@jugl-web/rest-api";
import { InfoAlertDialog } from "@jugl-web/ui-kit";
import { ModuleChangeList } from "./components/ModuleChangeList";
import heroImg from "./assets/hero-img.png";

export const EntityModulesChangesDialog: React.FC = () => {
  const { t } = useTranslations();
  const modulesData = useWorkspaceFeatureCardData();
  const { entityId } = useEntitySelectedProvider();
  const { me } = useMe();

  const [isOpen, setIsOpen] = useState(false);
  const [isDiffChecked, setIsDiffChecked] = useState(false);
  const [moduleDiff, setModuleDiff] = useState<{
    added: string[];
    removed: string[];
  } | null>(null);

  const [enabledModulesByEntityId, setEnabledModulesByEntityId] =
    useLocalStorage<Record<string, string[] | undefined>>(
      ENABLED_ENTITY_MODULES_KEY,
      {}
    );

  const enabledModules = useMemo(
    () => enabledModulesByEntityId[entityId],
    [enabledModulesByEntityId, entityId]
  );

  const modules = useMemo(
    () =>
      Object.entries(modulesData)
        .filter(([, feature]) => feature.isCoreFeature || feature.isEnabled)
        .map(([key]) => key),
    [modulesData]
  );

  useEffect(() => {
    if (!isDiffChecked && me?.role) {
      if (
        enabledModules &&
        (me?.role === UserRole.admin || me?.role === UserRole.member)
      ) {
        const added = modules.filter((mod) => !enabledModules.includes(mod));
        const removed = enabledModules.filter((mod) => !modules.includes(mod));

        if (added.length > 0 || removed.length > 0) {
          setModuleDiff({ added, removed });
          setIsOpen(true);
        }
      }
      setIsDiffChecked(true);
    }
  }, [enabledModules, isDiffChecked, me?.role, modules]);

  useEffect(() => {
    if (isDiffChecked && !isEqual(enabledModules, modules)) {
      setEnabledModulesByEntityId({
        [entityId]: modules,
      });
    }
  }, [
    modules,
    enabledModules,
    isDiffChecked,
    setEnabledModulesByEntityId,
    entityId,
  ]);

  useEffect(() => {
    setIsDiffChecked(false);
  }, [entityId]);

  return (
    <InfoAlertDialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      img={heroImg}
      title={t({
        id: "workspace-features-page.workspace-got-tweaked",
        defaultMessage: "Your workspace just got tweaked",
      })}
      content={
        <div className="flex flex-col">
          <span>
            {t({
              id: "workspace-features-page.workspace-settings-recently-updated-2",
              defaultMessage:
                "Admin of the Workspace recently updated list of modules that can be used:",
            })}
          </span>
          {!!moduleDiff?.added.length && (
            <ModuleChangeList items={moduleDiff.added} type="added" />
          )}
          {!!moduleDiff?.removed.length && (
            <ModuleChangeList items={moduleDiff.removed} type="removed" />
          )}
        </div>
      }
      buttons={[
        {
          text: t({
            id: "common.got-it",
            defaultMessage: "Got It",
          }),
          role: "close",
        },
      ]}
    />
  );
};
